import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  AnswerChoice,
  AnswerType,
  LibraryAction,
  LibraryEntry,
  LibraryEntryDetail,
  QuestionnaireDetail,
  QuestionnaireItem,
  QuestionnaireMode,
  QuestionnaireRow,
  QuestionnaireStage,
} from './types'

export const BASE = '/api/regulatory/questionnaires'
const KEY = 'questionnaires'

const WORKING: QuestionnaireStage[] = ['extracting', 'matching']
export const isWorking = (stage: QuestionnaireStage | undefined) =>
  !!stage && WORKING.includes(stage)

function useInvalidate() {
  const qc = useQueryClient()
  return () => void qc.invalidateQueries({ queryKey: [KEY] })
}

export function useQuestionnaires(filters: {
  stage?: string
  mode?: QuestionnaireMode
  q?: string
}) {
  const params = new URLSearchParams()
  if (filters.stage) params.set('stage', filters.stage)
  if (filters.mode) params.set('mode', filters.mode)
  if (filters.q) params.set('q', filters.q)
  return useQuery({
    queryKey: [KEY, 'list', filters],
    queryFn: () =>
      jsonFetch<{ items: QuestionnaireRow[]; total: number; can_edit: boolean }>(
        `${BASE}?${params.toString()}`,
      ),
    // Uploads are read in the background: poll while any is still being read.
    refetchInterval: (query) =>
      query.state.data?.items.some((r) => isWorking(r.stage)) ? 4_000 : false,
    staleTime: 10_000,
  })
}

export function useQuestionnaire(id: string | null) {
  return useQuery({
    queryKey: [KEY, 'detail', id],
    queryFn: () => jsonFetch<QuestionnaireDetail>(`${BASE}/${id}`),
    enabled: id !== null,
    refetchInterval: (query) => (isWorking(query.state.data?.stage) ? 3_000 : false),
  })
}

export interface UploadVars {
  file: File
  mode: QuestionnaireMode
  title: string
  requester_name: string
  received_on?: string
  due_on?: string
}

export function useUploadQuestionnaire() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (vars: UploadVars) => {
      const form = new FormData()
      form.append('file', vars.file)
      form.append('mode', vars.mode)
      form.append('title', vars.title)
      form.append('requester_name', vars.requester_name)
      if (vars.received_on) form.append('received_on', vars.received_on)
      if (vars.due_on) form.append('due_on', vars.due_on)
      return jsonFetch<QuestionnaireRow>(BASE, { method: 'POST', rawBody: form })
    },
    onSuccess: invalidate,
  })
}

export interface QuestionnaireUpdateVars {
  id: string
  title?: string
  requester_name?: string | null
  received_on?: string | null
  due_on?: string | null
  assigned_to?: string | null
  notes?: string | null
}

export function useUpdateQuestionnaire() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: ({ id, ...body }: QuestionnaireUpdateVars) =>
      jsonFetch<QuestionnaireDetail>(`${BASE}/${id}`, { method: 'PATCH', body }),
    onSuccess: invalidate,
  })
}

export function useDeleteQuestionnaire() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (id: string) => jsonFetch<void>(`${BASE}/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  })
}

/** retry | rematch | approve | reopen | sent */
export function useQuestionnaireAction() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: ({
      id,
      action,
    }: {
      id: string
      action: 'retry' | 'rematch' | 'approve' | 'reopen' | 'sent'
    }) => jsonFetch<QuestionnaireDetail>(`${BASE}/${id}/${action}`, { method: 'POST' }),
    onSuccess: invalidate,
  })
}

export interface ItemUpdateVars {
  questionnaireId: string
  itemId: string
  answer_choice?: AnswerChoice | ''
  answer_text?: string
  library_action?: LibraryAction | ''
  use_entry_id?: string
  accept?: boolean
}

export function useUpdateItem() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: ({ questionnaireId, itemId, ...body }: ItemUpdateVars) =>
      jsonFetch<QuestionnaireItem>(`${BASE}/${questionnaireId}/items/${itemId}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: invalidate,
  })
}

export function useAcceptItems() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: ({ questionnaireId, itemIds }: { questionnaireId: string; itemIds: string[] }) =>
      jsonFetch<QuestionnaireDetail>(`${BASE}/${questionnaireId}/accept`, {
        method: 'POST',
        body: { item_ids: itemIds },
      }),
    onSuccess: invalidate,
  })
}

// ─── Library ─────────────────────────────────────────────────────────

export function useLibrary(filters: {
  q?: string
  review_due?: boolean
  include_inactive?: boolean
}) {
  const params = new URLSearchParams()
  if (filters.q) params.set('q', filters.q)
  if (filters.review_due) params.set('review_due', 'true')
  if (filters.include_inactive) params.set('include_inactive', 'true')
  return useQuery({
    queryKey: [KEY, 'library', filters],
    queryFn: () =>
      jsonFetch<{ items: LibraryEntry[]; total: number; can_edit: boolean }>(
        `${BASE}/library?${params.toString()}`,
      ),
    staleTime: 15_000,
  })
}

export function useLibraryEntry(id: string | null) {
  return useQuery({
    queryKey: [KEY, 'library', 'entry', id],
    queryFn: () => jsonFetch<LibraryEntryDetail>(`${BASE}/library/${id}`),
    enabled: id !== null,
  })
}

export interface EntryVars {
  canonical_question?: string
  answer_type?: AnswerType
  answer_choice?: AnswerChoice | ''
  answer_text?: string
  tags?: string[]
  volatile?: boolean
  review_due?: string | null
  active?: boolean
}

export function useCreateEntry() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (body: EntryVars) =>
      jsonFetch<LibraryEntry>(`${BASE}/library`, { method: 'POST', body }),
    onSuccess: invalidate,
  })
}

export function useUpdateEntry() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: ({ id, ...body }: EntryVars & { id: string }) =>
      jsonFetch<LibraryEntry>(`${BASE}/library/${id}`, { method: 'PATCH', body }),
    onSuccess: invalidate,
  })
}

export function useDeleteVariant() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: ({ entryId, variantId }: { entryId: string; variantId: string }) =>
      jsonFetch<void>(`${BASE}/library/${entryId}/variants/${variantId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  })
}

export function useMergeEntries() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: ({ keepId, mergeId }: { keepId: string; mergeId: string }) =>
      jsonFetch<{ wordings_moved: number }>(`${BASE}/library/${keepId}/merge`, {
        method: 'POST',
        body: { merge_id: mergeId },
      }),
    onSuccess: invalidate,
  })
}
